import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, EMPTY } from 'rxjs';
import { AuthenticationService } from 'src/app/api-service/authentication.service';
import { RfidService } from 'src/app/api-service/rfid.service';
import { UserService } from 'src/app/api-service/user.service';
import { ErrorMsg } from 'src/app/helpers/contants';
import { LoginStatusCode, MfaStatusCode } from 'src/app/helpers/enums';
import { environment } from 'src/environments/environment';
import { ErrorModalComponent } from '../../shared-module/error-modal/error-modal.component';
import { MatDialog } from '@angular/material';
import { catchError, first, flatMap, map } from 'rxjs/operators';
import { MfaService } from 'src/app/api-service/mfa.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  @ViewChild(ErrorModalComponent, { static: false }) errorModal: ErrorModalComponent;
  @ViewChild('mfaModal', { static: false }) mfaModal;
  @ViewChild('accountLockedModal', { static: false }) accountLockedModal;
  @ViewChild('commonErrorModal', { static: false }) commonErrorModal;

  routeSub: Subscription;
  getUserSub: Subscription;
  getProfileSub: Subscription;
  postUserSub: Subscription;

  loginForm: FormGroup;
  verifyForm: FormGroup;

  privacyPolicy: string = environment.privacyPolicy;
  emailRegexp = environment.emailRegexp;
  maxEmailLength = environment.maxEmailLength;
  maxPassLength = environment.maxPasswordLength;
  returnURL: string;
  isSubmit: boolean = false;
  isLoading: boolean = false;

  enableResendCode: boolean = true;
  attemptCounter: number = 0;
  interval: any;
  maxAttempt: number = 0; // threshold
  countdown: number = 0; // 125
  mfaResp: any;
  errorMsg: any;

  loginFormErrorMsgList = {
    requireEmail: ErrorMsg.requireEmail,
    invalidEmail: ErrorMsg.invalidEmail,
    accNotExist: ErrorMsg.accNotExist,
    requirePassword: ErrorMsg.requirePassword,
    incorrectPassword: ErrorMsg.incorrectPassword,
    maxFailedAttempts: ErrorMsg.maxFailedAttempts,
    exceedLengthEmail: ErrorMsg.maxEmailLength
  }

  verifyFormErrorMsgList = {
    requireCode: ErrorMsg.requireCode,
    invalidLengthCode: ErrorMsg.invalidLengthCode
  }

  constructor(private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthenticationService,
    private userService: UserService,
    private dialog: MatDialog,
    private mfaService: MfaService,
    private rfidSrv: RfidService) { }

  ngOnInit() {
    this.routeSub = this.route.queryParams.subscribe(
      params => {
        this.returnURL = params['returnUrl'];
      });

    this.constructLoginForm();
    this.constructOtpForm();

    this.verifyForm.get('code').valueChanges
      .subscribe(value => {
        this.errorMsg = '';
      });
  }

  ngOnDestroy() {
    this.clearInterval();

    if (this.routeSub)
      this.routeSub.unsubscribe();

    if (this.getUserSub)
      this.getUserSub.unsubscribe();

    if (this.getProfileSub) {
      this.getProfileSub.unsubscribe();
    }

    if (this.postUserSub)
      this.postUserSub.unsubscribe();
  }

  get lf() { return this.loginForm.controls; }

  get f() {
    return this.verifyForm.controls;
  }

  constructLoginForm() {
    this.loginForm = this.formBuilder.group({
      email: new FormControl('', [Validators.required, Validators.pattern(this.emailRegexp), Validators.maxLength(this.maxEmailLength)]),
      password: new FormControl('', [Validators.required])
    });
  }

  constructOtpForm() {
    this.verifyForm = this.formBuilder.group({
      code: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]]
    });
  }

  onLoginFormSubmit() {
    if (!this.loginForm.valid) {
      return;
    }

    this.isLoading = true;

    const formData = { ...this.loginForm.value };
    formData.password = this.authService.hashString(formData.password);

    this.postUserSub = this.authService.login(formData).subscribe(
      (resp) => {
        if (resp.mfaEnabled) {
          this.attemptCounter = resp.otpAttempt;
          this.maxAttempt = resp.maxOtpAttempt;

          this.authService.requestOtp()
            .pipe(
              catchError(e => {
                // console.log(e);
                if (e.statusCode == MfaStatusCode.AccountStillLocked) {
                  const dialogRef2 = this.dialog.open(this.accountLockedModal, {
                    width: 'auto',
                    disableClose: true,
                    hasBackdrop: true
                  });
                } else {
                  const dialogRef3 = this.dialog.open(this.commonErrorModal, {
                    width: 'auto',
                    disableClose: true,
                    hasBackdrop: true
                  });
                }
                return EMPTY;
              }))
            .subscribe((response) => {
              this.tickTock();
              const dialogRef = this.dialog.open(this.mfaModal, {
                width: 'auto', // You can also set the width here if you prefer
                disableClose: true,
                hasBackdrop: true
              });
            });
        } else {
          this.completeLogin(formData);
        }
      },
      (err) => {
        if (err == LoginStatusCode.AccountPendingActivation)
          this.errorModal.openModal("Account is not activated", "resend-activation");
        else
          this.errorModal.openModal(err);

        this.isLoading = false;
      });
  }

  dismiss() {
    this.dialog.closeAll();
  }

  refresh() {
    location.reload();
  }

  tickTock() {
    console.log('ticktock');
    let timer = environment.otpTimer;
    this.interval = setInterval(() => {
      this.countdown = --timer;
      if (this.countdown === 0) {
        this.clearInterval();
        this.enableResendCode = false;
      }
    }, 1000);
  }

  clearInterval() {
    clearInterval(this.interval);
  }

  resend() {
    this.enableResendCode = !this.enableResendCode;
    this.authService.requestOtp()
      .pipe(
        catchError(e => {
          // console.log(e);
          const dialogRef3 = this.dialog.open(this.commonErrorModal, {
            width: 'auto',
            disableClose: true,
            hasBackdrop: true
          });
          return EMPTY;
        }))
      .subscribe((response) => {
        this.tickTock();
      });
    // this.authService.requestOtp()
    //   .subscribe(
    //     response => {
    //       console.log(response);
    //       // this.attemptCounter = this.attemptCounter+1;
    //       // if (this.attemptCounter !== 5) {
    //       //   this.tickTock();
    //       // }
    //       this.tickTock();
    //     });
  }

  verifyOtp() {
    const formData = { ...this.loginForm.value };
    formData.password = this.authService.hashString(formData.password);

    this.isLoading = true;

    this.authService.verifyOtp(this.verifyForm.value)
      .pipe(
        catchError(e => {
          console.log(e);
          if (e.statusCode == MfaStatusCode.ExpiredOrInvalidCode) {
            this.errorMsg = e.statusMessage;
            this.addCounter();
            if (this.attemptCounter >= this.maxAttempt) {
              const dialogRef2 = this.dialog.open(this.accountLockedModal, {
                width: 'auto',
                disableClose: true,
                hasBackdrop: true
              });
              // this.toastr.error('Attempts exceeded 5 times, please try again after 5 minutes')
              //   .onHidden
              //   .subscribe(() => this.router.navigate(['/login']));
            }
          } else {
            const dialogRef3 = this.dialog.open(this.commonErrorModal, {
              width: 'auto',
              disableClose: true,
              hasBackdrop: true
            });
          }
          return EMPTY;
        }))
      .subscribe((response) => {
        this.dialog.closeAll();
        this.completeLogin(formData);
      });
  }

  addCounter() {
    this.attemptCounter = this.attemptCounter + 1;
    sessionStorage.setItem('otpAttemptCounter', this.attemptCounter.toString());
    return this.attemptCounter;
  }

  completeLogin(formData: any) {
    this.getProfileSub = this.userService.getProfile().subscribe(
      (res) => {
        // const isPilot = res.isPilot;

        // if (environment.IRS_whiteList) {
        //   if (isPilot) {
        //     this.rfidSrv.saveIsPilotValue(true);
        //   } else {
        //     this.rfidSrv.saveIsPilotValue(false);
        //   }
        // } else {
        //   this.rfidSrv.saveIsPilotValue(true);
        // }

        this.rfidSrv.saveCustomerId(res.customerAccountId);
        this.rfidSrv.saveCustomerInfo(JSON.stringify(res));

        if (res.hasTempPwd) {
          this.authService.setHasTempPassword("true", formData.password);
          this.router.navigate(['/set-new-password'], { queryParams: { returnUrl: this.returnURL } });
          return;
        }
        else {
          if (this.returnURL) {
            this.router.navigateByUrl(this.returnURL);
          } else {
            // const dialogRef = this.dialog.open(this.mfaModal, {
            //   width: 'auto', // You can also set the width here if you prefer
            // });
            this.dismiss();
            this.router.navigate(['/dashboard']);
          }
        }
      },
      (err) => {
        // this.errorModal.openModal(err);
        this.isLoading = false;
      });
  }
}
